'use client'

import React, { useState } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { AnimatePresence, motion } from 'framer-motion'

interface ColorFilterProps {
  attributeCode: string
  label: string
  icon?: string
  defaultOpen?: boolean
  options: AggregationsOption[]
  selectedValues: string[]
  onChange: (attributeCode: string, value: string) => void
}

/**
 * Component filter dạng color picker với hiệu ứng collapse
 */
export const ColorFilter: React.FC<ColorFilterProps> = ({
  attributeCode,
  label,
  icon,
  defaultOpen = false,
  options,
  selectedValues,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <Stack sx={{ mb: 1 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        onClick={() => setIsOpen(!isOpen)}
        sx={{ cursor: 'pointer', userSelect: 'none', py: 1 }}
      >
        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'text.primary',
          }}
        >
          {icon && <Box component="span">{icon}</Box>}
          {label}
        </Typography>
        <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.3 }}>
          <KeyboardArrowDownIcon sx={{ color: 'text.secondary' }} />
        </motion.div>
      </Stack>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            style={{ overflow: 'hidden' }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 1,
                pt: 1,
                pb: 1,
              }}
            >
              {options.map((option) => {
                const isSelected = selectedValues.includes(option.label)
                const hexColor = option.swatch_data?.value || '#cccccc'

                return (
                  <Stack
                    key={option.label}
                    onClick={() => onChange(attributeCode, option.label)}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 0.5,
                      cursor: 'pointer',
                      userSelect: 'none',
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        bgcolor: hexColor,
                        border: isSelected ? `2px solid` : `1px solid #eee`,
                        borderColor: isSelected ? 'primary.main' : '#eee',
                        flexShrink: 0,
                        transition: 'all 0.2s',
                        transform: isSelected ? 'scale(1.1)' : 'scale(1)',
                        boxShadow: isSelected ? '0 0 0 2px #fff inset' : 'none',
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: '11px',
                        textAlign: 'center',
                        fontWeight: isSelected ? 600 : 400,
                        color: isSelected ? 'primary.main' : 'text.secondary',
                        lineHeight: 1.2,
                      }}
                    >
                      {option.label}
                    </Typography>
                  </Stack>
                )
              })}
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Stack>
  )
}
